/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
import { Trans, withI18n } from '@lingui/react'
import classNames from 'clsx'
import deepmerge from 'deepmerge'
import { bool, shape } from 'prop-types'
import { parseUrl, stringifyUrl } from 'query-string'
import { PureComponent } from 'react'
import { compose } from 'redux'

import {
  ABO_LABEL,
  GTM_EVENT_CLICK_PRODUCT_BUTTON,
  PRODUCT_STATUS_AVAILABLE,
  TENANT_IT,
} from 'shared/consts'
import { AD_SERVER_METADATA_DATA_ATTR } from 'shared/consts/adServer'
import {
  GTM_EVENT_CLICK_PRODUCT,
  GTM_EVENT_CLICK_PRODUCT_IMAGE,
} from 'shared/consts/gtm'
import { OFFER_TYPE_SAE } from 'shared/consts/marketplace'
import { PAGE_TYPE_SEARCH_RESULTS } from 'shared/consts/page'
import { PRICE_TYPES } from 'shared/consts/productTemplates'
import SerpMobileLayoutItV1Item from 'shared/experiments/components/SerpMobileLayoutIT/SerpMobileLayoutItV1Item'
import SerpMobileLayoutItV2Item from 'shared/experiments/components/SerpMobileLayoutIT/SerpMobileLayoutItV2Item'
import {
  CAT_REMOVE_CTA_AND_VARIANTS,
  EXPERIMENT_VARIATION,
  SERP_MOBILE_LAYOUT_IT,
  SERP_REMOVE_CTA_AND_VARIANTS,
  TOP_DEALS_LABEL,
} from 'shared/experiments/consts'
import { getExperiment } from 'shared/experiments/utils/experiments'
import {
  isMarketplace,
  isVariantAttributes,
} from 'shared/experiments/utils/featureFlags'
import { isRebrandingITActive } from 'shared/experiments/utils/rebranding'
import store from 'shared/store'
import { setOfferType, setProductUrl } from 'shared/store/ducks/events'
import { isMobile } from 'shared/utils/deviceClassUtils'
import { isCdnUrl } from 'shared/utils/imageUtils'
import { isEmpty } from 'shared/utils/objectUtils'
import {
  getPriceForType,
  getProductTemplate,
} from 'shared/utils/productTemplateUtils'
import { normalizeUrl } from 'shared/utils/url'
import DiscountBadge from 'views/components/atoms/DiscountBadge'
import IconArrowRight from 'views/components/atoms/Icon/IconArrowRight'
import Price from 'views/components/atoms/Price'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import LinkWithIcon from 'views/components/molecules/LinkWithIcon'
import ModalShowButton from 'views/components/molecules/ModalShowButton'
import StarRating from 'views/components/molecules/StarRating'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import ProductAction from 'views/components/organisms/FilteredProductList/SearchProductListItem/ProductAction'
import ProductStatus from 'views/components/organisms/ProductStatus'
import SellerBox from 'views/components/organisms/SellerBox'
import { shouldRenderTopDealLabel } from 'views/components/organisms/SliderHorizontal/ProductSliderItem/helpers'
import TransWithHtml from 'views/components/utils/TransWithHtml'
import { promotionTypeMapping } from 'views/consts/promotions'
import withMountPoints from 'views/enhancers/withMountPoints'
import { withSearchStateAndUrlContext } from 'views/enhancers/withSearchStateAndUrlContext'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { getDiscountToShow } from 'views/utils/discount'
import {
  isHideAboSubscription,
  isRedPointsActive,
} from 'views/utils/featureToggleUtils'
import {
  isDiscountValid,
  isListPriceGreaterThanOrEqualToRetailPrice,
  isPricePresent,
  isProductAvailable,
  isProductOutOfStock,
  isSingleVariantAttributeSERP,
} from 'views/utils/productUtils'
import { i18nType, mountPointsType, ProductType } from 'views/utils/propTypes'
import { adServerMetadata } from 'views/utils/propTypes/adserver'

import { CLASS_SELECTABLE } from './consts'
import {
  collectLabelsProps,
  getDummyRedpointsData,
  hasPersonalVariantAttributes,
  isSaeOffer,
  shouldRenderAboLabel,
  shouldRenderAbPrice,
  shouldRenderCta,
  shouldRenderProductDetailLink,
  shouldRenderProductLabels,
  shouldRenderVariants,
} from './helpers'
import IdSwitcher from './IdSwitcher'
import ProductVariants from './ProductVariants'
import SearchProductLabels from './SearchProductLabels'
import './SearchProductListItem.scss'
import SearchProductListItemComponent from './SearchProductListItemComponent'

class SearchProductListItem extends PureComponent {
  state = this.getInitialState()

  isVariantsToggleEnabled() {
    const {
      publicRuntimeConfig: {
        pageProperties: { experiments = [] },
      },
    } = this.props

    return isVariantAttributes(experiments)
  }

  getNumberOfVariants() {
    return this.props.product.variants?.length || 0
  }

  shouldShowProductVariantsBox() {
    const shouldShowVariants =
      this.props.shouldShowVariantButtonsOnHits &&
      this.props.product.variants?.length > 1

    if (this.isVariantsToggleEnabled()) {
      return (
        shouldShowVariants &&
        isSingleVariantAttributeSERP(this.props.product.variants)
      )
    }

    return shouldShowVariants
  }

  isItemWithMultipleVariantAttributes() {
    return (
      this.isVariantsToggleEnabled() &&
      !isSingleVariantAttributeSERP(this.props.product.variants) &&
      this.getNumberOfVariants() > 1
    )
  }

  getVariantDiscount({ stockStatus, discount } = {}) {
    return isProductOutOfStock(stockStatus) ? null : discount
  }

  getPriceInfo(currentVariant) {
    const {
      product: {
        id,
        flags: { prescriptionMedicine },
        variants = [],
      },
      publicRuntimeConfig: {
        pageProperties: { pageType, experiments },
      },
    } = this.props

    const usedVariant = this.isItemWithMultipleVariantAttributes()
      ? this.getCheapestVariant(variants) || currentVariant
      : currentVariant
    const shouldDisplayAbPrice = shouldRenderAbPrice(experiments, pageType)
    return {
      productId: id,
      stockStatus: usedVariant.stockStatus,
      unitPricing: usedVariant.unitPricing,
      prices: usedVariant.prices,
      prescriptionMedicine,
      shouldDisplayAbPrice,
    }
  }

  getCheapestVariant(variants) {
    const availableVariants = variants.filter(variant => {
      return (
        variant.stockStatus &&
        variant.stockStatus.status === PRODUCT_STATUS_AVAILABLE
      )
    })

    if (!availableVariants || availableVariants.length < 1) {
      return undefined
    }

    const sortedVariants = availableVariants.sort((a, b) => {
      const amountA = a.best_offer?.price?.amount
      const amountB = b.best_offer?.price?.amount

      if (!amountA && !amountB) {
        return 0
      }
      if (!amountA) {
        return 1
      }
      if (!amountB) {
        return -1
      }

      return amountA - amountB
    })
    return sortedVariants[0]
  }

  getInitialState() {
    const {
      product,
      product: {
        deeplink,
        descriptionShort,
        canonicalURL,
        id,
        icd10,
        ods_partners,
        imageUrl,
        labels,
        manufacturer,
        multiplierRP,
        name,
        nowProductGroup,
        pharmaForm,
        promotion,
        promotionProduct,
        unit,
        unitQuantityString,
        // FIXME rename in mapper
        offers_count: offerCount = 0,
        seller_count: sellerCount = 0,
        best_offer: bestOffer,
        ean,
        pzn,
        upid,
        isbn,
        best_offer: {
          type,
          promotion: bestOfferPromotion,
          redpoints: { multiplier } = {},
        } = {},
        variants = [],
        objectID,
        metadataId,
      },
      publicRuntimeConfig: {
        publicConfig: { tenant },
        pageProperties: { pageType, experiments, featureToggles },
      },
    } = this.props

    const offerId = bestOffer?.id

    const sellerBoxParams = {
      sellerName: bestOffer?.seller?.name,
      sellerId: bestOffer?.seller?.id,
      offerType: bestOffer?.type,
      cssBoxClasses: {
        padding: 'u-padding-small',
        textAlign: 'u-text-align--left@medium',
        backgroundColor: 'u-background-color--greySky',
      },
      embedSellerModal: false,
    }

    const labelsWithoutAboLabel = labels.filter(
      labelName => labelName !== ABO_LABEL
    )

    const offerTypeSae = isSaeOffer({
      bestOffer,
      experiments,
      tenant,
    })
    const hideAboSubscription = isHideAboSubscription(featureToggles)

    const displayAboLabel =
      labels &&
      shouldRenderAboLabel({ offerTypeSae, labels }) &&
      !hideAboSubscription

    const currentVariant =
      variants.find(variant => variant.id === id) || product
    return {
      currentVariantId: id,
      discount: this.getVariantDiscount(currentVariant),
      packageDetails: {
        unit,
        unitQuantityString,
        pharmaForm,
        id,
        manufacturer,
      },
      priceInfo: this.getPriceInfo(currentVariant),
      promotion: promotion || promotionTypeMapping[bestOfferPromotion?.type],
      promotionProduct,
      canonicalURL: normalizeUrl(deeplink) || normalizeUrl(canonicalURL),
      isSearchPage: pageType === PAGE_TYPE_SEARCH_RESULTS,
      offers: {
        count: offerCount,
        currentOfferId: offerId,
      },
      offerTypeSae,
      descriptionShort,
      displayAboLabel,
      imageUrl,
      labelsWithoutAboLabel,
      multiplierRP: multiplierRP || multiplier,
      name,
      ods_partners,
      nowProductGroup,
      ean,
      pzn,
      upid,
      icd10,
      isbn,
      type,
      sellerCount,
      bestOffer,
      sellerBoxParams,
      experiments,
      featureToggles,
      objectID,
      metadataId,
    }
  }

  extractVariantProps = ({
    canonicalURL,
    descriptionShort,
    discount,
    id,
    imageUrl,
    labels,
    multiplierRP,
    nowProductGroup,
    pharmaForm,
    prices,
    productName,
    promotion,
    promotionProduct,
    stockStatus,
    unit,
    pzn,
    isbn,
    ean,
    unitPricing,
    unitQuantityString,
    // FIXME rename in mapper
    offers_count: offerCount = 0,
    seller_count: sellerCount = 0,
    best_offer: {
      id: offerId,
      type,
      promotion: bestOfferPromotion,
      redpoints: { multiplier } = {},
    } = {},
    best_offer: bestOffer,
    sellerBoxParams = {
      sellerName: bestOffer?.seller?.name,
      sellerId: bestOffer?.seller?.id,
      offerType: bestOffer?.type,
      cssBoxClasses: {
        padding: 'u-padding-small',
        textAlign: 'u-text-align--left@medium',
        backgroundColor: 'u-background-color--greySky',
      },
      embedSellerModal: false,
    },
    objectID,
    metadataId,
  }) => {
    const {
      publicRuntimeConfig: {
        pageProperties: { featureToggles },
      },
    } = this.props

    const hideAboSubscription = isHideAboSubscription(featureToggles)

    return {
      currentVariantId: id,
      discount: isProductOutOfStock(stockStatus) ? null : discount,
      packageDetails: {
        pharmaForm,
        unit,
        unitQuantityString,
        id,
      },
      priceInfo: { productId: id, stockStatus, prices, unitPricing },
      name: productName,
      displayAboLabel:
        Boolean(labels?.includes(ABO_LABEL)) && !hideAboSubscription,
      labelsWithoutAboLabel:
        labels?.filter(labelName => labelName !== ABO_LABEL) || [],
      offers: {
        count: offerCount,
        currentOfferId: offerId,
      },
      promotion: promotion || promotionTypeMapping[bestOfferPromotion?.type],
      promotionProduct,
      canonicalURL,
      descriptionShort,
      bestOffer,
      imageUrl,
      multiplierRP: multiplierRP || multiplier,
      nowProductGroup,
      type,
      pzn,
      isbn,
      ean,
      sellerCount,
      sellerBoxParams,
      objectID,
      metadataId,
    }
  }

  handleVariantUpdate = variant => {
    const {
      publicRuntimeConfig: {
        publicConfig: { tenant },
        pageProperties: { experiments, featureToggles },
      },
    } = this.props
    const newState = this.extractVariantProps(variant)
    const { labelsWithoutAboLabel, displayAboLabel, multiplierRP } = newState
    const hideAboSubscription = isHideAboSubscription(featureToggles)

    const offerTypeSae = isSaeOffer({
      bestOffer: variant?.best_offer,
      experiments,
      tenant,
    })

    // Merge the props except of prices:
    // priceInfo contains also stock status
    this.setState(prevState => ({
      ...deepmerge(prevState, newState),
      displayAboLabel: displayAboLabel && !hideAboSubscription,
      labelsWithoutAboLabel,
      multiplierRP,
      offerTypeSae,
      priceInfo: {
        ...prevState.priceInfo,
        prices: newState.priceInfo.prices,
        stockStatus: newState.priceInfo.stockStatus,
        unitPricing: newState.priceInfo.unitPricing,
      },
      offers: {
        count: newState.offers.count,
        currentOfferId: newState.offers.currentOfferId,
      },
    }))
  }

  hasAddToCartButton = (stockStatus, { count = 0, currentOfferId }) => {
    const {
      product: {
        flags: { prescriptionMedicine },
      },
      publicRuntimeConfig: {
        publicConfig: { tenant },
        pageProperties: { experiments, pageType },
      },
    } = this.props

    const hasMarketplaceEnabledWithOffersAvailable =
      isMarketplace(experiments, tenant) && !(count === 1 && currentOfferId)

    if (!shouldRenderCta(experiments, pageType)) {
      return false
    }

    if (
      hasMarketplaceEnabledWithOffersAvailable ||
      this.isItemWithMultipleVariantAttributes()
    ) {
      return false
    }

    return !prescriptionMedicine && !isProductOutOfStock(stockStatus)
  }

  getProductTemplate() {
    const {
      product,
      publicRuntimeConfig: {
        publicConfig: { tenant },
      },
    } = this.props

    return getProductTemplate({ ...product, tenant })
  }

  getProductUrl = eventName => {
    const {
      queryParams: searchQueryParams,
      product: { variant_attributes },
      adServerMetadata,
    } = this.props
    const { isSearchPage, metadataId } = this.state
    let { canonicalURL } = this.state
    const adServerMetadataContent = adServerMetadata[metadataId] || {}
    const { trackingUrls = {} } = adServerMetadataContent
    const clickUrl = trackingUrls?.clickUrl

    if (clickUrl) {
      canonicalURL = clickUrl
    }

    if (isSearchPage) {
      const personalVariantAttributesQueryParameter =
        this.isItemWithMultipleVariantAttributes() &&
        hasPersonalVariantAttributes(variant_attributes)
          ? { master: 'true' }
          : {}

      const newQueryParams = {
        eventName,
        ...searchQueryParams,
        ...personalVariantAttributesQueryParameter,
      }

      const parsedProductUrl = parseUrl(canonicalURL, { decode: false })
      const query = {
        ...newQueryParams,
        ...parsedProductUrl.query,
      }

      const { url } = parsedProductUrl

      return stringifyUrl(
        {
          url,
          query,
        },
        { encode: false }
      )
    }

    return `${canonicalURL}`
  }

  getOffersUrl = () => {
    const { mountPoints } = this.props
    const { isSearchPage, currentVariantId } = this.state

    const url = mountPoints.getOffersUrl(currentVariantId)

    if (isSearchPage) {
      return `${url}?eventType=view&eventName=viewOffers_search`
    }

    return url
  }

  handleProductAboLabelClick = () => {
    store.dispatch(setProductUrl({ productUrl: this.state.canonicalURL }))
    store.dispatch(setOfferType({ offerType: this.state.type }))
  }

  handleProductCardClick = event => {
    if (event.currentTarget != event.target) {
      return
    }
    const {
      publicRuntimeConfig: {
        publicConfig: { deviceClass },
      },
    } = this.props
    const { target } = event
    const isElemSellerBox = target.getAttribute('data-target') === 'seller-box'
    const isSelectable =
      !isMobile(deviceClass) &&
      (target.closest(`.${CLASS_SELECTABLE}`) || isElemSellerBox)
    const shouldRedirect =
      target.tagName.toLowerCase() !== 'a' &&
      !target.closest('a') &&
      !isElemSellerBox

    if (isSelectable) {
      event.preventDefault()
    } else if (shouldRedirect) {
      window.location.href = this.getProductUrl(GTM_EVENT_CLICK_PRODUCT)
    }
  }

  getRenderConditionals({ listPrice, retailPrice }) {
    const {
      product,
      product: {
        flags: { prescriptionMedicine },
        ratingCount,
      },
      publicRuntimeConfig: {
        publicConfig: { discountBoundaries, tenant },
        pageProperties: { experiments, pageType },
      },
    } = this.props

    const {
      discount,
      nowProductGroup,
      offers,
      offers: { count: offersCount },
      priceInfo: { stockStatus = product.stockStatus },
      type,
      featureToggles,
    } = this.state

    const shouldDisplayAbPrice = shouldRenderAbPrice(experiments, pageType)

    const hasOffers = offersCount > 1
    let shouldDisplayNowWidget = nowProductGroup === 'sameday_nextday'
    if (shouldDisplayNowWidget && isMarketplace(experiments, tenant)) {
      shouldDisplayNowWidget = type === OFFER_TYPE_SAE
    }

    return {
      hasAddToCartButton: this.hasAddToCartButton(stockStatus, offers),
      isAvailable: isProductAvailable(stockStatus),
      isListPricePresent:
        !prescriptionMedicine &&
        isListPriceGreaterThanOrEqualToRetailPrice({ listPrice, retailPrice }),
      isRetailPricePresent: isPricePresent(retailPrice),
      isValidDiscount: isDiscountValid(discount, discountBoundaries),
      shouldDisplayLinkToOfferPage:
        !this.isItemWithMultipleVariantAttributes() &&
        hasOffers &&
        isMarketplace(experiments, tenant),
      shouldDisplayMpOrMultiVariantsLayout:
        this.isItemWithMultipleVariantAttributes() ||
        (isMarketplace(experiments, tenant) && hasOffers) ||
        shouldDisplayAbPrice,
      shouldDisplayNowWidget,
      shouldDisplayRatings: !prescriptionMedicine && ratingCount > 0,
      shouldShowSellerBox: offersCount === 1,
      isRedPointsFeatureToggleActive: isRedPointsActive(featureToggles),
    }
  }

  render() {
    const {
      hideStockStatusOnMobile,
      isFirst,
      enableSponsoredLabel,
      product: {
        flags: { prescriptionMedicine },
        prescriptionType,
        rating,
        ratingCount,
        sponsoredProductLabel,
        variants,
        highlighted,
        highlightedProductLabel,
        thumbnail,
        redPoints,
        redPointsIsActive,
        redPointsMultiplierIsValid,
        srcHeight,
        srcWidth,
      },
      lazyLoadImage,
      publicRuntimeConfig,
      publicRuntimeConfig: {
        publicConfig,
        publicConfig: { superScripts, tenant, onlineDoctorConfig, deviceClass },
        pageProperties: {
          isSubscriptionProduct,
          experiments: featureExperiments,
          pageType,
        },
      },
      queryParams,
      showDiscountLabel,
      adServerMetadata,
      i18n,
    } = this.props
    const { currentVariantId, metadataId } = this.state
    const currentVariant =
      variants.find(variant => variant.id === currentVariantId) || {}
    const product = {
      ...this.props.product,
      ...currentVariant,
      metadataId,
    }
    const {
      descriptionShort,
      discount,
      ean,
      pzn,
      isbn,
      imageUrl,
      offerTypeSae,
      bestOffer,
      offers: { currentOfferId },
      packageDetails: {
        id,
        manufacturer,
        pharmaForm,
        unit,
        unitQuantityString,
      },
      priceInfo: { prices, stockStatus = product.stockStatus, unitPricing },
      ods_partners,
      multiplierRP,
      name,
      sellerCount,
      upid,
      sellerBoxParams,
      experiments,
      isSearchPage,
    } = this.state
    const productTemplate = this.getProductTemplate()
    const listPrice = getPriceForType(prices, PRICE_TYPES.LIST)
    const retailPrice = getPriceForType(prices, PRICE_TYPES.RETAIL)
    const supNoteNumber = superScripts?.supNoteNumber
    const discountsToShow = getDiscountToShow({ discount, tenant })

    const adServerMetadataContent = adServerMetadata[metadataId] || {}
    const { trackingUrls = {} } = adServerMetadataContent
    const topDealExperiment = getExperiment(TOP_DEALS_LABEL, experiments)
    const shouldHaveTopDealLabel = shouldRenderTopDealLabel(
      topDealExperiment,
      product.id
    )
    const removeCtaAndVariantsCatExperiment = getExperiment(
      CAT_REMOVE_CTA_AND_VARIANTS,
      experiments
    )
    const removeCtaAndVariantsSerpExperiment = getExperiment(
      SERP_REMOVE_CTA_AND_VARIANTS,
      experiments
    )

    const shouldShowVariantsForExperiment = shouldRenderVariants(
      experiments,
      pageType
    )
    const shouldShowProductLinkForExperiment = shouldRenderProductDetailLink(
      experiments,
      pageType,
      tenant
    )
    const shouldShowCTAForExperiment = shouldRenderCta(experiments, pageType)
    const {
      hasAddToCartButton,
      isAvailable,
      isListPricePresent,
      isRetailPricePresent,
      isValidDiscount,
      shouldDisplayLinkToOfferPage,
      shouldDisplayMpOrMultiVariantsLayout,
      shouldDisplayRatings,
      shouldShowSellerBox,
      isRedPointsFeatureToggleActive,
    } = this.getRenderConditionals({ listPrice, retailPrice })

    let redPointsData
    if (isRedPointsFeatureToggleActive && multiplierRP) {
      redPointsData = getDummyRedpointsData(multiplierRP)
    }
    const discountLabel = (
      <>
        <span>&minus;{discount}%</span>
        {supNoteNumber && <sup>{supNoteNumber}</sup>}
      </>
    )
    const discountLabelParam = showDiscountLabel && isValidDiscount

    const labelsProps = collectLabelsProps(
      product,
      enableSponsoredLabel,
      publicRuntimeConfig,
      adServerMetadata,
      discountLabelParam
    )
    const isRebrandingEnabled = isRebrandingITActive(featureExperiments)

    const retailPriceClasses = classNames(
      'u-no-margin o-SearchProductListItem__prices__retail-price',
      {
        'u-color--red': isValidDiscount,
        'a-h2':
          (tenant === TENANT_IT && shouldShowCTAForExperiment) ||
          tenant !== TENANT_IT,
        'l-flex': tenant === TENANT_IT && !shouldShowCTAForExperiment,
      }
    )

    const itemClassName = classNames(
      'o-SearchProductListItem u-padding--top u-background-color--background u-position--relative u-border u-border-radius',
      {
        'o-SearchProductListItem--first':
          isFirst && shouldRenderProductLabels(labelsProps),
        'o-SearchProductListItem__highlighted': highlighted,
        'o-SearchProductListItem__isTopDeal': shouldHaveTopDealLabel,
      }
    )

    const textInfoClassName = classNames(
      'u-padding--left l-flex l-flex--vertical-stretch u-border--left@medium l-col--12-of-12 l-col--5-of-12@medium o-SearchProductListItem__content__body',
      {
        'l-flex--distribute-space': descriptionShort,
        'l-flex--justify-content--end': !descriptionShort,
      }
    )

    const stockStatusClassName = classNames(
      'a-h4-tiny u-margin-small--bottom@small-tiny',
      {
        'u-hidden@small-tiny': hideStockStatusOnMobile && isAvailable,
      }
    )

    const titleClassName = classNames(
      'o-SearchProductListItem__title u-font-weight--bold u-no-margin a-h3@medium',
      {
        'u-margin-xx-small--bottom': shouldDisplayRatings,
      }
    )
    const priceClassName = classNames(
      'u-margin-xx-small--left o-SearchProductListItem__prices__list-price u-text-decoration--line-through'
    )
    /**
     * It is convenient to define react components as constants below here,
     * because they are small and there are a lot of them. It is easier to keep
     * them in one file, as split to many small files and hand over plenty of properties
     * every time
     */
    const renderProductItemLinkWithIcon = (
      <>
        {this.isItemWithMultipleVariantAttributes() && (
          <div className="o-PointerOnHover">
            <LinkWithIcon
              href={this.getProductUrl(GTM_EVENT_CLICK_PRODUCT)}
              icon={<IconArrowRight className="u-color--blue" />}
              right
            >
              <TransWithHtml
                text={i18n._('product.variants.configure.link')}
                className="u-font-weight--bold u-color--blue u-no-wrap o-PointerOnHover__text"
              />
            </LinkWithIcon>
          </div>
        )}
        {shouldDisplayLinkToOfferPage && (
          <div className="o-PointerOnHover">
            <LinkWithIcon
              href={this.getOffersUrl()}
              icon={<IconArrowRight className="u-color--blue" />}
              right
            >
              <TransWithHtml
                text={i18n._('product.seller.count', { count: sellerCount })}
                className="u-font-weight--bold u-color--blue u-no-wrap o-PointerOnHover__text"
              />
            </LinkWithIcon>
          </div>
        )}
      </>
    )

    const renderGoToProductLink = !this.isItemWithMultipleVariantAttributes() &&
      !shouldDisplayLinkToOfferPage && (
        <div className="o-PointerOnHover">
          <LinkWithIcon
            href={this.getProductUrl(GTM_EVENT_CLICK_PRODUCT)}
            icon={<IconArrowRight className="u-color--blue" />}
            right
            className="u-font-weight--bold"
          >
            <Trans id="general.button.label.productLink" />
          </LinkWithIcon>
        </div>
      )

    const renderProductVariantsBox = this.shouldShowProductVariantsBox() &&
      shouldShowVariantsForExperiment && (
        <ProductVariants
          currentVariantId={currentVariantId}
          variants={variants}
          updateCurrentVariant={this.handleVariantUpdate}
        />
      )

    const searchProductListItemProps = {
      'data-qa-id': 'result-list-entry',
      'data-issubscriptionproduct': isSubscriptionProduct,
      'data-clientside-hook': 'FilteredProductListItem',
      [AD_SERVER_METADATA_DATA_ATTR]: !isEmpty(adServerMetadataContent)
        ? JSON.stringify(adServerMetadataContent || {})
        : undefined,
    }

    const productUrl = this.getProductUrl(GTM_EVENT_CLICK_PRODUCT)
    const productUrlWithImageEvent = this.getProductUrl(
      GTM_EVENT_CLICK_PRODUCT_IMAGE
    )

    const listPriceWithDiscount = isListPricePresent && isValidDiscount && (
      <p className="l-flex u-no-margin o-SearchProductListItem__prices__list-price-wrapper">
        <div className="o-SearchProductListItem__prices__list-price__text">
          {offerTypeSae ? (
            <Trans id={listPrice?.info?.text} />
          ) : (
            <Trans id="general.label.miraklListPrice" />
          )}
          <sup>{listPrice?.info?.index}</sup>
        </div>
        <span className={priceClassName}>
          {offerTypeSae ? (
            <Price {...listPrice} />
          ) : (
            bestOffer?.strike_price?.formatted
          )}
        </span>
      </p>
    )

    const retailPriceComponent = isRetailPricePresent && (
      <p className={retailPriceClasses} data-qa-id="entry-price">
        {shouldDisplayMpOrMultiVariantsLayout && (
          <>
            <Trans id="general.label.price.from" />
            &nbsp;
          </>
        )}
        <Price {...retailPrice} />
      </p>
    )

    const idSwitcher = (
      <li className="u-color--grey u-hidden@small-tiny o-SearchProductListItem__info-list__item">
        <span
          className="a-h4-tiny o-SearchProductListItem__info__items"
          suppressHydrationWarning
        >
          <IdSwitcher pzn={pzn} ean={ean} upid={upid} uid={id} isbn={isbn} />
        </span>
      </li>
    )

    const productStatus = (
      <a href={productUrl} draggable={false} className="a-link--no-underline">
        <ProductStatus
          stockStatus={stockStatus}
          dot
          className={stockStatusClassName}
        />
      </a>
    )

    const unitPricingComponent = unitPricing && (
      <p className="u-no-margin u-margin-xx-small--bottom u-no-wrap u-color--grey o-SearchProductListItem__prices__unitPricing">
        {unitPricing}
      </p>
    )

    const addToCartButtonComponent = (
      <ProductAction
        hasAddToCartButton={hasAddToCartButton && shouldShowCTAForExperiment}
        algoliaQueryId={queryParams.queryID}
        offerId={currentOfferId}
        position={queryParams.position}
        prescriptionType={prescriptionType}
        productId={currentVariantId}
        productTemplate={productTemplate}
        stockStatus={stockStatus}
        trackingUrls={trackingUrls}
        productUrl={this.getProductUrl(GTM_EVENT_CLICK_PRODUCT_BUTTON)}
        showProductDetailLink={
          tenant === TENANT_IT && shouldShowProductLinkForExperiment
        }
        mobile={
          removeCtaAndVariantsSerpExperiment?.isEnabled ||
          removeCtaAndVariantsCatExperiment?.isEnabled
            ? !shouldShowProductLinkForExperiment
            : true
        }
      />
    )

    const starRatingComponent = shouldDisplayRatings && (
      <StarRating
        {...{
          rating,
          ratingCount,
          ratingCountColor: 'dark',
          href: this.getProductUrl(GTM_EVENT_CLICK_PRODUCT),
          hasSuperScript: true,
        }}
      />
    )

    const searchProductLabelsComponent = (
      <SearchProductLabels
        {...labelsProps}
        handleProductAboLabelClick={this.handleProductAboLabelClick}
        discountLabel={discountLabel}
        sponsoredProductLabel={sponsoredProductLabel}
      />
    )

    const odsPartnersComponent = ods_partners &&
      ods_partners.length > 0 &&
      onlineDoctorConfig?.enabled && (
        <div
          id="odsModalButtonSERP"
          data-odspartners={`odsModalProduct-${JSON.stringify(ods_partners)}`}
          data-clientside-hook="ods-modal-trigger-buttonSERP"
          className="odsModalbuttonDiv u-color--blue u-padding-xx-large--bottom u-font-weight--bold"
        >
          <ModalShowButton
            component={ButtonAsLink}
            id="odsModalButton"
            name="buybox-large_online-doctor-service"
            modalId={`modalIdOdsSERP`}
            onClick={() => {}}
          >
            <TextWithIcon
              id="odsModalButton"
              qaId="show-ods-modal-button-serp"
              className="u-color--blue"
              icon={<IconArrowRight className="u-color--blue" />}
              right
            >
              <Trans id="buyBox.link.label.onlineDoctorService.zava" />
            </TextWithIcon>
          </ModalShowButton>
        </div>
      )

    const discountBadge =
      !showDiscountLabel && discountsToShow ? (
        <DiscountBadge
          discount={discount}
          ellipse
          rounded
          smaller
          className="u-margin-small--right u-font-weight--bold"
        >
          <span suppressHydrationWarning className="u-no-wrap">
            &minus;{discount}%
          </span>
        </DiscountBadge>
      ) : null
    /**
     * The function is for experiment CRO-2020. The standard experiment
     * component doesn't work here, because the whole FilteredProdctList
     * component is hydrated. This combination evokes javascript memory
     * leak.
     *
     * The function will be removed, as soon as it is clear, what variant
     * wins.
     */
    const searchProductListItem = props => {
      const serpMobileLayoutITExperiment = getExperiment(
        SERP_MOBILE_LAYOUT_IT,
        experiments
      )

      if (
        serpMobileLayoutITExperiment.isEnabled &&
        isMobile(deviceClass) &&
        isSearchPage
      ) {
        switch (serpMobileLayoutITExperiment.variant) {
          case EXPERIMENT_VARIATION.V1:
            return <SerpMobileLayoutItV1Item {...props} />
          case EXPERIMENT_VARIATION.V2:
            return <SerpMobileLayoutItV2Item {...props} />
        }
      }
      return <SearchProductListItemComponent {...props} />
    }

    const searchProductListItemComponentProps = {
      searchProductListItemProps,
      className: itemClassName,
      highlighted,
      highlightedProductLabel,
      searchProductLabelsComponent,
      currentOfferId,
      isItemWithMultipleVariantAttributes:
        this.isItemWithMultipleVariantAttributes(),
      quantityUnit: `${unitQuantityString} ${unit}`,
      pharmaForm,
      handleProductCardClick: this.handleProductCardClick,
      imageProps: {
        prescriptionMedicine,
        discount,
        imageUrl,
        multiplierRP,
        name,
        prescriptionType,
        prices,
        productTemplate,
        redPoints,
        redPointsIsActive,
        redPointsMultiplierIsValid,
        srcHeight,
        srcWidth,
        thumbnail,
      },
      redpointsMultiplier: redPointsData?.multiplierRP,
      pzn,
      lazyLoadImage,
      productUrl,
      productUrlWithImageEvent,
      titleClassName,
      isCdnUrl: isCdnUrl(publicConfig, imageUrl),
      starRatingComponent,
      idSwitcher,
      manufacturer,
      productStatus,
      isRebrandingEnabled,
      renderProductItemLinkWithIcon,
      textInfoClassName,
      descriptionShort,
      retailPriceComponent,
      unitPricingComponent,
      listPriceWithDiscount,
      addToCartButtonComponent,
      odsPartnersComponent,
      discountBadge,
      sellerBoxComponent: shouldShowSellerBox &&
        isMarketplace(experiments, tenant) && (
          <SellerBox {...sellerBoxParams} />
        ),
      renderProductVariantsBox,
      renderGoToProductLink,
      pageType,
    }
    return searchProductListItem(searchProductListItemComponentProps)
  }
}

SearchProductListItem.propTypes = {
  i18n: i18nType.isRequired,
  hideStockStatusOnMobile: bool,
  isFirst: bool,
  enableSponsoredLabel: bool,
  mountPoints: mountPointsType.isRequired,
  product: shape(ProductType).isRequired,
  lazyLoadImage: bool.isRequired,
  publicRuntimeConfig: shape({}),
  queryParams: shape({}),
  showDiscountLabel: bool,
  shouldShowVariantButtonsOnHits: bool,
  adServerMetadata,
}

SearchProductListItem.defaultProps = {
  hideStockStatusOnMobile: false,
  isFirst: false,
  enableSponsoredLabel: true,
  publicRuntimeConfig: {},
  queryParams: {},
  showDiscountLabel: false,
  shouldShowVariantButtonsOnHits: true,
  adServerMetadata: {},
}

export default compose(
  withSearchStateAndUrlContext,
  withShopAndRuntimeConfig,
  withMountPoints,
  withI18n()
)(SearchProductListItem)
